import { unwrap } from "@sagot/api-client";
import { DEFAULT_AUDIENCE_LABELS } from "@sagot/constants";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { fetchAllSources } from "../../../lib/all-sources";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "./keys";
import type { AgentConfig, AgentListOut, IndustryTemplate, Member, ModelOption, PlaygroundConfig, SourceView, Team } from "./types";

const STALE_MS = 30_000;
const CATALOG_STALE_MS = 5 * 60_000;

/** `GET /v1/agents`: agents with 7-day stats + the plan's agent allowance. */
export function useAgentList(): UseQueryResult<AgentListOut> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: buildKeys.agents(ws), queryFn: () => unwrap(api.GET("/v1/agents")), staleTime: STALE_MS });
}

/** `GET /v1/agents/{id}`: the full Agent Settings config (effective retrieval values). */
export function useAgentConfig(agentId: string | null): UseQueryResult<AgentConfig> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.agent(ws, agentId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}", { params: { path: { agent_id: agentId ?? "" } } })),
    enabled: Boolean(agentId),
  });
}

/** `GET /v1/agents/{id}/playground-config`: what the Playground renders (needs playground.use only). */
export function usePlaygroundConfig(agentId: string | null): UseQueryResult<PlaygroundConfig> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.playgroundConfig(ws, agentId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/playground-config", { params: { path: { agent_id: agentId ?? "" } } })),
    enabled: Boolean(agentId),
  });
}

/** `GET /v1/agents/models`: chat models with plan availability and credit weight (needs agents.view). */
export function useModels(enabled = true): UseQueryResult<ModelOption[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: buildKeys.models(ws), queryFn: () => unwrap(api.GET("/v1/agents/models")), enabled, staleTime: CATALOG_STALE_MS });
}

export function useTemplates(): UseQueryResult<IndustryTemplate[]> {
  const api = useApi();
  return useQuery({ queryKey: buildKeys.templates, queryFn: () => unwrap(api.GET("/v1/industry-templates")), staleTime: CATALOG_STALE_MS });
}

/** `GET /v1/teams` (optional: callers degrade when the member lacks team.view). */
export function useTeams(enabled = true): UseQueryResult<Team[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: buildKeys.teams(ws), queryFn: () => unwrap(api.GET("/v1/teams")), enabled, retry: false, staleTime: CATALOG_STALE_MS });
}

const MEMBERS_LIMIT = 200;

/** `GET /v1/members` (optional: needs team.view). Active members only: who a handoff can go to. */
export function useAssignableMembers(enabled = true): UseQueryResult<Member[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.assignableMembers(ws),
    queryFn: async () => (await unwrap(api.GET("/v1/members", { params: { query: { limit: MEMBERS_LIMIT } } }))).items.filter((m) => m.status === "active"),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** `GET /v1/sources` (optional: needs knowledge.view). */
export function useSources(enabled = true): UseQueryResult<SourceView[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: buildKeys.sources(ws), queryFn: () => fetchAllSources(api), enabled, retry: false, staleTime: STALE_MS });
}

type AudienceRow = Awaited<ReturnType<typeof listAudiences>>[number];

async function listAudiences(api: ReturnType<typeof useApi>) {
  return unwrap(api.GET("/v1/audiences"));
}

/** Tenant audience labels by code (Amendment A); falls back to the default labels without audiences.view. */
export function useAudienceLabels(): (code: number) => string {
  const api = useApi();
  const ws = useWorkspaceId();
  const query = useQuery({ queryKey: ["ws", ws, "audiences"] as const, queryFn: () => listAudiences(api), retry: false, staleTime: CATALOG_STALE_MS });
  const rows: readonly AudienceRow[] = query.data ?? [];
  const defaults: Readonly<Record<number, string | undefined>> = DEFAULT_AUDIENCE_LABELS;
  return (code: number) => rows.find((a) => a.code === code)?.label ?? defaults[code] ?? String(code);
}
